<template>
  <v-content class="admin ats page">
    <can I="ats" a="Opportunity">
      <v-container>
        <v-card v-if="opportunity && orderedPhases[0]">
          <v-toolbar
            height="80"
            extension-height="130"
            style="justify-content: center;"
            color="transparent"
          >
            <div class="text-xs-center">
              <div class="title" v-html="opportunity.organization.name" />
              <div class="headline" v-html="opportunity.name" />
            </div>

            <div
              class="ats phase tabs"
              style="position: relative; width: 100%;"
              v-if="get(phase, 'name')"
              slot="extension"
            >
              <v-tabs
                centered
                hide-slider
                ref="tabs"
                :value="phase.name"
                height="130"
                next-icon=""
                prev-icon=""
              >
                <v-tab
                  v-for="tab in [...orderedPhases, ...pushedPhases]"
                  :key="tab.name"
                  :href="`#${tab.name}`"
                >
                  <v-tooltip bottom>
                    <span>{{ tab.description }}</span>
                    <v-card
                      class="text-xs-center py-3"
                      tile
                      flat
                      :height="130"
                      :width="200"
                      :class="{ 'phase-card': tab.name !== phase.name }"
                      :color="tab.name === phase.name ? 'grey darken-2' : ''"
                      slot="activator"
                    >
                      <v-layout column fill-height class="pt-2" @click="changePhase(tab)">
                        <v-flex>
                          <v-badge left color="info">
                            <span slot="badge">{{ (counts && counts[tab.name]) || 0 }}</span>
                            <v-icon large>mdi-account-group</v-icon>
                          </v-badge>
                        </v-flex>
                        <v-flex>
                          <v-card-text class="subheading py-0" v-html="tab.name" />
                        </v-flex>
                      </v-layout>
                      <div class="bottom-border" v-if="tab.name === phase.name"></div>
                    </v-card>
                  </v-tooltip>
                </v-tab>
                <v-menu v-model="showMoreMenu" offset-y class="v-tabs__div">
                  <v-card
                    slot="activator"
                    tile
                    flat
                    class="text-xs-center py-3 v-tabs__item phase-card"
                    :class="{ 'phase-card--active': showMoreMenu }"
                    :height="130"
                    :width="200"
                  >
                    <v-layout column fill-height class="pt-2">
                      <v-flex>
                        <v-icon large>mdi-chevron-down</v-icon>
                      </v-flex>
                      <v-flex>
                        <v-card-text class="subheading py-0">MORE</v-card-text>
                      </v-flex>
                    </v-layout>
                  </v-card>
                  <v-list>
                    <v-list-tile
                      v-for="tab in pushablePhases"
                      :key="`more-${tab.name}`"
                      @click="changePhase(tab)"
                    >
                      <v-list-tile-avatar>
                        <v-icon>mdi-account-group</v-icon>
                      </v-list-tile-avatar>
                      <v-list-tile-content class="pr-1">
                        <v-list-tile-title v-html="tab.name" />
                      </v-list-tile-content>
                      <v-list-tile-action>
                        <v-chip small class="info">
                          <strong>{{ (counts && counts[tab.name]) || 0 }}</strong>
                        </v-chip>
                      </v-list-tile-action>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-tabs>
              <v-btn
                v-if="prevIconVisible"
                @click="scrollLeft"
                small
                fab
                class="scroll-button left"
              >
                <v-icon large>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                v-if="nextIconVisible"
                @click="scrollRight"
                small
                fab
                class="scroll-button right"
              >
                <v-icon large>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </v-toolbar>
          <can I="ats" a="Opportunity">
            <ApplicationsListData
              :filters="filters"
              v-if="type(phase) === 'applications'"
              :loading="loading"
            >
              <v-card slot-scope="{ applications }">
                <ApplicationsToolbar :applications="applications" :phase="phase" />
                <v-tabs-items v-if="get(phase, 'name')" :value="phase.name" @input="phase">
                  <v-tab-item
                    lazy
                    v-for="tab in [...orderedPhases, ...pushedPhases]"
                    :key="tab.name"
                    :value="tab.name"
                  >
                    <ApplicationCard
                      v-for="application in applications"
                      :key="`application-${application.id}`"
                      :application="application"
                      :phase="phase"
                      class="mb-0"
                      @click="toggleView"
                    />
                  </v-tab-item>
                </v-tabs-items>
                <ApplicationDialog v-if="view" :id="parseInt(view)" @close="toggleView($event)" />
              </v-card>
            </ApplicationsListData>
          </can>

          <can I="list" a="Eligibility">
            <EligibilitiesListData
              :filters="filters"
              v-if="type(phase) === 'eligibilities'"
              :loading="loading"
            >
              <v-card slot-scope="{ eligibilities, loading }">
                <EligibilitiesToolbar :eligibilities="eligibilities" :phase="phase" />
                <v-tabs-items v-if="get(phase, 'name')" :value="phase.name" @input="changePhase">
                  <v-tab-item lazy v-for="tab in pushedPhases" :key="tab.name" :value="tab.name">
                    <EligibilityCard
                      v-for="eligibility in eligibilities"
                      :key="`eligibility-${eligibility.id}`"
                      :eligibility="eligibility"
                      :phase="phase"
                      class="mb-0"
                      @click="toggleView"
                    />
                  </v-tab-item>
                </v-tabs-items>
                <can I="read" a="Eligibility">
                  <EligibilityDialog
                    v-if="!loading && view"
                    :id="parseInt(view)"
                    @close="toggleView($event)"
                  />
                </can>
              </v-card>
            </EligibilitiesListData>
          </can>
        </v-card>
      </v-container>
    </can>
  </v-content>
</template>

<script>
// Components
import ApplicationCard from '@/components/applications_tracker/applications/ApplicationCard'
import ApplicationsListData from '@/components/applications_tracker/applications/ApplicationsListData'
import EligibilitiesListData from '@/components/applications_tracker/eligibilities/EligibilitiesListData'
import EligibilitiesToolbar from '@/components/applications_tracker/eligibilities/EligibilitiesToolbar'
import EligibilityDialog from '@/components/applications_tracker/eligibilities/EligibilityDialog'
import EligibilityCard from '@/components/applications_tracker/eligibilities/EligibilityCard'
import ApplicationsToolbar from '@/components/applications_tracker/applications/ApplicationsToolbar'
import ApplicationDialog from '@/components/applications_tracker/applications/ApplicationDialog'

// Helpers
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'

// Mixins
import { managedQueryParameters } from '@/mixins/managedQueryParameters.js'

export default {
  name: 'ApplicationsTracker',
  components: {
    ApplicationCard,
    ApplicationDialog,
    ApplicationsListData,
    ApplicationsToolbar,
    EligibilitiesListData,
    EligibilitiesToolbar,
    EligibilityCard,
    EligibilityDialog,
  },

  mixins: [managedQueryParameters],

  created() {
    // Fetch opportunity
    this.$store
      .dispatch('admin/opportunities/fetch', this.id)
      .then(() => {
        // Set initial phase
        if (
          'status' in this.initialQueryParameters &&
          this.findPhase(this.initialQueryParameters.status[0])
        ) {
          this.changePhase(this.initialQueryParameters.status[0])
        } else if (this.orderedPhases[0] && !this.phase) {
          this.changePhase(this.orderedPhases[0])
        }

        // Fetch application and update filters
        if (this.$route.query['view'] && this.type(this.phase) === 'applications') {
          this.$store
            .dispatch('admin/applications/fetch', this.$route.query['view'])
            .then(({ data }) => {
              if (data.status !== this.phase) {
                this.changePhase(data.status)
              }
              this.view = data.id
            })
        }

        // Fetch eligibility and update filters
        if (this.$route.query['view'] && this.type(this.phase) === 'eligibilities') {
          this.$store
            .dispatch('admin/eligibilities/fetch', this.$route.query['view'])
            .then(({ data }) => {
              if (this.phase === data.invite ? 'Invited' : 'Qualifying') {
                this.changePhase(!!data.invite ? 'Invited' : 'Qualifying')
              }
              this.view = data.id
            })
        }
      })
      .finally(() => {
        this.initScrollButtonAttributes()
        // Load applications
        if (!this.loadingApplications)
          this.$store
            .dispatch('admin/applications/load', { opportunity: this.$route.params.id })
            .then(() =>
              this.$store.dispatch('admin/applications/updateFilters', {
                ...this.filters,
                ...this.params,
              })
            )
        // Load eligibilities
        if (!this.loadingEligibilities)
          this.$store
            .dispatch('admin/eligibilities/load', { opportunity: this.$route.params.id })
            .then(() =>
              this.$store.dispatch('admin/eligibilities/updateFilters', {
                ...this.filters,
                ...this.params,
              })
            )
      })
  },

  beforeDestroy() {
    this.$store.dispatch('admin/applications/abandon')
    this.$store.dispatch('admin/eligibilities/abandon')
    this.$store.dispatch('admin/applications/resetState')
    this.$store.dispatch('admin/eligibilities/resetState')

    if (this.resizeObserver) this.resizeObserver.unobserve(this.$refs['tabs'].$el)
  },

  props: ['id'],

  data() {
    return {
      currentTabIndex: 0,
      filters: {},
      moreEligibilities: [{ name: 'Qualifying' }, { name: 'Invited' }],
      phase: null,
      pushedPhases: [],
      showMoreMenu: false,
      nextIconVisible: false,
      prevIconVisible: false,
      view: null,
    }
  },

  computed: {
    ...mapGetters({
      orderedPhases: 'admin/opportunities/orderedPhases',
      opportunities: 'admin/opportunities/all',
      progress: 'admin/applications/progress',
    }),

    ...mapState('admin/opportunities', {
      counts: 'counts',
      loadingOpportunities: 'loading',
    }),

    ...mapState('admin/applications', {
      loadingApplications: 'loading',
    }),

    ...mapState('admin/eligibilities', {
      loadingEligibilities: 'loading',
    }),

    opportunity() {
      return this.$store.getters['admin/opportunities/find']()
    },

    loading() {
      return this.loadingOpportunities || this.loadingApplications || this.loadingEligibilities
    },

    moreApplicationPhases() {
      if (this.opportunity && this.opportunity.syntheticPhases) {
        return this.opportunity.syntheticPhases
      }
      return []
    },

    pushablePhases() {
      if ([...this.moreEligibilities, ...this.moreApplicationPhases]) {
        return [...this.moreEligibilities, ...this.moreApplicationPhases].filter(
          available =>
            ![...this.orderedPhases, ...this.pushedPhases].some(
              unavailable => available.name === unavailable.name
            )
        )
      }
      return []
    },
  },

  watch: {
    phase: {
      handler: 'setScrollButtonAttributes',
    },
  },

  methods: {
    get(object, path) {
      return _.get(object, path)
    },

    findPhase(
      phase,
      phases = [...this.orderedPhases, ...this.moreEligibilities, ...this.moreApplicationPhases]
    ) {
      if (Number(phase)) {
        return phases.find(p => p.id === Number(phase))
      }
      if (phase && phase.id) {
        return phases.find(p => p.id === phase.id)
      }
      if (phase && phase.name) {
        return phases.find(p => p.name === phase.name)
      }
      return phases.find(p => p.name === phase)
    },

    changePhase(phase) {
      const newPhase = this.findPhase(phase)
      if (newPhase) {
        this.pushPhase(newPhase)
        this.filters.page = 1
        this.updateFilters({ status: newPhase.name })
        this.phase = newPhase
      }
    },

    pushPhase(phase) {
      const newPhase = this.findPhase(phase, this.pushablePhases)
      if (newPhase) {
        this.pushedPhases.push(newPhase)
      }
    },

    updateFilters(changes) {
      if (changes) {
        Object.keys(changes).forEach(change => {
          this.$set(this.filters, change, changes[change])
        })
        this.updateQueryParameters(changes)
      }
    },

    scrollRight() {
      this.$refs['tabs'].scrollTo('next')
      this.setScrollButtonAttributes()
    },

    scrollLeft() {
      this.$refs['tabs'].scrollTo('prev')
      this.setScrollButtonAttributes()
    },

    initScrollButtonAttributes() {
      if (!this.$refs['tabs']) setTimeout(this.initScrollButtonAttributes, 100)
      this.setScrollButtonAttributes()
    },

    setScrollButtonAttributes() {
      if (!this.$refs['tabs']) {
        return
      }

      if (!this.resizeObserver && !!ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.setScrollButtonAttributes)
        this.resizeObserver.observe(this.$refs['tabs'].$el)
      }

      this.$nextTick(() => {
        this.nextIconVisible = this.$refs['tabs'].checkNextIcon()
        this.prevIconVisible = this.$refs['tabs'].checkPrevIcon()
      })
    },

    toggleView(id) {
      this.view = id ? id : null
      this.updateQueryParameters({ view: this.view })
    },

    type(phase) {
      if (!this.opportunity.phases || !phase) {
        return
      }
      return [...this.opportunity.phases, ...this.opportunity.syntheticPhases].some(
        p => p.name === phase.name
      )
        ? 'applications'
        : 'eligibilities'
    },
  },
}
</script>

<style>
.ats.page div.overlay {
  position: relative;
}

.ats.page .v-window .v-window__container.v-window__container--is-active {
  height: unset !important;
}

.ats.page div.overlay:after {
  position: absolute;
  content: '';
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 2;
}

.ats.page div.v-toolbar__content {
  justify-content: center;
}

.ats.page div.v-toolbar__extension,
.ats.page div.v-toolbar__extension .v-tabs__item {
  padding-left: 0;
  padding-right: 0;
}

.ats.page .ats.phase.tabs .ats-tabs {
  max-width: 100%;
  overflow-x: scroll;
  cursor: pointer;
  scroll-behavior: smooth;
}

.ats.page .ats.phase.tabs .bottom-border {
  position: absolute;
  width: 100%;
  height: 0.5rem;
  bottom: 0px;
}

.ats.page .ats.phase.tabs .phase-card {
  background-color: rgba(0, 0, 0, 0);
  border: 0px;
}

.ats.page .ats.phase.tabs .phase-card:hover,
.ats.page .ats.phase.tabs .phase-card--active {
  background-color: rgba(0, 0, 0, 0.2);
}

.ats.page .ats.phase.tabs .scroll-button {
  position: absolute;
  top: 50%;
}

.ats.page .ats.phase.tabs .scroll-button.left {
  left: 0px;
  transform: translate(-50%, -50%);
}

.ats.page .ats.phase.tabs .scroll-button.right {
  right: 0px;
  transform: translate(50%, -50%);
}

.ats.page .ats.phase.tabs .v-tabs__wrapper--show-arrows {
  margin-right: 0px;
  margin-left: 0px;
}

.ats.page .ats.phase.tabs .v-badge__badge {
  width: 100%;
  border-radius: 20px;
}
</style>
